/* Petal Cart Drawer
 * Open triggers:
 *   - Click on any element matching `[data-petal-cart-open]` or `[href="/cart"]` (header cart icon).
 *   - Successful POSTs to /cart/add (any form on the page); the form's submit
 *     is hijacked, the request is sent via fetch to /cart/add.js, and on success
 *     the drawer opens.
 * Mutations: quantity +/- and remove call /cart/change.js, then re-render.
 * Shipping protection toggle adds/removes the configured variant ID.
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var drawer = document.querySelector('[data-petal-cart-drawer]');
    if (!drawer) return;
    var configEl = document.getElementById('petal-cart-drawer-config');
    var config = {};
    if (configEl) {
      try { config = JSON.parse(configEl.textContent); } catch (e) {}
    }
    var threshold = parseInt(config.freeShippingThreshold, 10) || 10000;
    var protectionVariantId = String(config.protectionVariantId || '');
    var protectionHandle = config.protectionHandle || 'shipping-protection';
    var protectionDefaultOn = config.protectionDefaultOn !== false; // default true
    var moneyFormat = config.moneyFormat || '${{amount}}';
    var DISMISS_KEY = 'petal_protection_dismissed';
    var protectionAutoApplied = false;

    // Set the protection price label client-side (Liquid math + Shopify
    // money-format both have edge cases that produce $5.0 instead of $5.00).
    var protectionPrice = parseInt(config.protectionPrice, 10) || 500;
    var protPriceEl = drawer.querySelector('[data-petal-cart-protection-price-display]');
    if (protPriceEl) {
      protPriceEl.textContent = '$' + (protectionPrice / 100).toFixed(2);
    }

    var panel        = drawer.querySelector('[data-petal-cart-panel]');
    var countEl      = drawer.querySelector('[data-petal-cart-count]');
    var itemsEl      = drawer.querySelector('[data-petal-cart-items]');
    var emptyEl      = drawer.querySelector('[data-petal-cart-empty]');
    var totalEl      = drawer.querySelector('[data-petal-cart-total]');
    var shipbar      = drawer.querySelector('[data-petal-cart-shipbar]');
    var shipbarText  = drawer.querySelector('[data-petal-cart-shipbar-text]');
    var shipbarFill  = drawer.querySelector('[data-petal-cart-shipbar-fill]');
    var protToggle   = drawer.querySelector('[data-petal-cart-protection]');
    var discountsEl  = drawer.querySelector('[data-petal-cart-discounts]');
    var confettiEl   = drawer.querySelector('[data-petal-cart-confetti]');
    var rewardsEl    = drawer.querySelector('[data-petal-cart-rewards]');
    var couponEl     = drawer.querySelector('[data-petal-cart-coupon]');
    var couponForm   = drawer.querySelector('[data-petal-cart-coupon-form]');
    var couponInput  = drawer.querySelector('[data-petal-cart-coupon-input]');
    var couponMsg    = drawer.querySelector('[data-petal-cart-coupon-msg]');
    var lastBelowThreshold = true; // Tracks free-shipping threshold crossings

    function formatMoney(cents) {
      cents = parseInt(cents, 10) || 0;
      var dollars = (cents / 100).toFixed(2);
      // Strip trailing " USD" Shopify sometimes appends.
      return ('$' + dollars).replace(/\s*USD\s*$/, '');
    }

    /* Body scroll lock — iOS-safe pattern. Plain `overflow: hidden` on body
       doesn't stop touch scroll in mobile Safari; the page behind the drawer
       still pans/flickers as the customer scrolls inside the drawer. The fix
       is to save the current scroll offset, freeze the body with
       `position: fixed; top: -scrollY`, then restore the scroll on close. */
    var savedScrollY = 0;
    function lockScroll() {
      if (document.documentElement.classList.contains('petal-cart-drawer-locked')) return;
      savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.documentElement.classList.add('petal-cart-drawer-locked');
    }
    function unlockScroll() {
      if (!document.documentElement.classList.contains('petal-cart-drawer-locked')) return;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.documentElement.classList.remove('petal-cart-drawer-locked');
      window.scrollTo(0, savedScrollY);
    }

    function show() {
      drawer.hidden = false;
      // Force reflow so the transition runs from initial state.
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('petal-cart-drawer-open');
      lockScroll();
    }
    function hide() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('petal-cart-drawer-open');
      unlockScroll();
      // Wait for the slide-out transition before hiding the element.
      setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 460);
    }

    function fetchCart() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
    }

    /* Shopify's /cart.js response does NOT include compare_at_price on line
       items (verified live). To show MSRP strikethroughs, we fetch each
       product's full data via /products/{handle}.js and build a per-variant
       cache, then patch each cart line item before rendering. */
    var variantCompareCache = Object.create(null);
    function enrichCartCompareAt(cart) {
      if (!cart || !cart.items || !cart.items.length) return Promise.resolve(cart);
      var handles = {};
      cart.items.forEach(function (item) {
        if (item.compare_at_price != null) return;
        if (variantCompareCache[item.variant_id] !== undefined) return;
        if (item.handle) handles[item.handle] = true;
      });
      var pending = Object.keys(handles).map(function (handle) {
        return fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (product) {
            if (!product || !product.variants) return;
            product.variants.forEach(function (v) {
              variantCompareCache[v.id] = v.compare_at_price || null;
            });
          })
          .catch(function () {});
      });
      return Promise.all(pending).then(function () {
        cart.items.forEach(function (item) {
          if (item.compare_at_price != null) return;
          var cached = variantCompareCache[item.variant_id];
          if (cached != null) item.compare_at_price = cached;
        });
        return cart;
      });
    }

    function addToCart(variantId, quantity) {
      var form = new FormData();
      form.append('id', variantId);
      form.append('quantity', quantity || 1);
      return fetch('/cart/add.js', { method: 'POST', body: form, headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); });
    }

    function changeLine(line, quantity) {
      var body = JSON.stringify({ line: line, quantity: quantity });
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: body
      }).then(function (r) { return r.json(); });
    }

    function changeByVariant(variantId, quantity) {
      var body = JSON.stringify({ id: variantId, quantity: quantity });
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: body
      }).then(function (r) { return r.json(); });
    }

    /* Compute the total minus the protection line — we don't want it to count
       toward free shipping or the displayed item subtotal. Also computes a
       compare-at total (sum of compare_at_price × qty across non-protection
       lines) so the secure-checkout button can show a struck-through MSRP. */
    function computeTotals(cart) {
      var total = cart.total_price || 0;
      var itemCount = cart.item_count || 0;
      var compareTotal = 0;
      cart.items.forEach(function (item) {
        var qty = item.quantity || 1;
        var lineCurrent = (item.final_line_price != null) ? item.final_line_price : (item.final_price * qty);
        var lineCompare = lineCurrent;
        if (item.compare_at_price && item.compare_at_price > item.final_price) {
          lineCompare = item.compare_at_price * qty;
        } else if (item.original_line_price && item.original_line_price > lineCurrent) {
          lineCompare = item.original_line_price;
        }
        // Protection is excluded from the items count + free-shipping bar
        // (it's a service, not a chosen product), but it IS included in the
        // compareTotal so the strikethrough on the SECURE CHECKOUT button
        // stays apples-to-apples with cart.total_price (which includes
        // protection). For protection lineCompare == lineCurrent (no MSRP).
        if (protectionVariantId && String(item.variant_id) === protectionVariantId) {
          total -= item.line_price;
          itemCount -= qty;
        }
        compareTotal += lineCompare;
      });
      return { total: total, itemCount: itemCount, compareTotal: compareTotal };
    }

    function render(cart) {
      var totals = computeTotals(cart);
      countEl.textContent = totals.itemCount;
      // Total + optional compare-at on the secure-checkout button.
      if (totals.compareTotal > cart.total_price) {
        totalEl.innerHTML =
          '<s class="petal-cart-drawer__checkout-compare">' + formatMoney(totals.compareTotal) + '</s> ' +
          formatMoney(cart.total_price);
      } else {
        totalEl.textContent = formatMoney(cart.total_price);
      }

      // Shipping bar
      var remaining = Math.max(0, threshold - totals.total);
      var pct = totals.total >= threshold ? 100 : Math.max(0, (totals.total / threshold) * 100);
      shipbarFill.style.width = pct + '%';
      var crossed = false;
      if (remaining <= 0) {
        shipbarText.innerHTML = '🎉 <strong>Surprise! You’ve unlocked FREE SHIPPING</strong>';
        if (lastBelowThreshold) crossed = true;
        lastBelowThreshold = false;
      } else {
        shipbarText.innerHTML = "You’re <strong>" + formatMoney(remaining) + "</strong> away from <strong>FREE SHIPPING</strong>";
        lastBelowThreshold = true;
      }
      if (crossed) fireFreeShippingCelebration();

      // Build a set of discount codes that are applied at the LINE-ITEM level
      // (i.e., to specific products). These get rendered INSIDE the item card
      // they apply to, and are excluded from the order-level discount block at
      // the bottom of the cart.
      var productLevelCodes = Object.create(null);
      (cart.items || []).forEach(function (it) {
        (it.line_level_discount_allocations || []).forEach(function (alloc) {
          var t = alloc && alloc.discount_application && alloc.discount_application.title;
          if (t) productLevelCodes[t.toUpperCase()] = true;
        });
      });

      // Discount codes (cart.discount_codes is the merchant-applied codes; we
      // also surface app-applied automatic discounts via cart_level_discount_applications).
      // Product-specific codes are filtered out here and rendered per-item below.
      renderDiscounts(cart, productLevelCodes);

      // Items
      itemsEl.innerHTML = '';
      var visibleItems = cart.items.filter(function (item) {
        return !protectionVariantId || String(item.variant_id) !== protectionVariantId;
      });
      // Stable sort using the sessionStorage variant→seq map. Lines whose
      // variant has no recorded seq (e.g., added via a different surface or
      // before this code shipped) sort BEFORE seq'd lines and keep their
      // relative cart order via Array.sort's stability guarantee.
      var orderMap = {};
      try { orderMap = JSON.parse(sessionStorage.getItem('petal_cart_order') || '{}') || {}; } catch (e) {}
      visibleItems.sort(function (a, b) {
        var aSeq = orderMap[String(a.variant_id)] || 0;
        var bSeq = orderMap[String(b.variant_id)] || 0;
        if (aSeq && bSeq) return aSeq - bSeq;
        if (aSeq && !bSeq) return 1;
        if (!aSeq && bSeq) return -1;
        return 0;
      });
      if (!visibleItems.length) {
        var p = document.createElement('p');
        p.className = 'petal-cart-drawer__empty';
        p.textContent = 'Your cart is empty.';
        itemsEl.appendChild(p);
      } else {
        visibleItems.forEach(function (item) {
          // Real cart line index (1-based) — we filter out protection but
          // /cart/change.js wants the original line number.
          var lineIndex = cart.items.indexOf(item) + 1;
          var row = document.createElement('div');
          row.className = 'petal-cart-drawer__item';
          row.setAttribute('role', 'listitem');
          row.setAttribute('data-line', lineIndex);

          var imgUrl = item.image
            ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_180x180$1$2')
            : '';
          var variantText = '';
          if (item.options_with_values && item.options_with_values.length) {
            variantText = item.options_with_values
              .filter(function (o) { return o.value && o.value.toLowerCase() !== 'default title'; })
              .map(function (o) { return o.name + ': ' + o.value; })
              .join(', ');
          }
          /* Per-line price block: line total (qty × final_price) plus an
             optional strikethrough when the variant has a compare-at price
             higher than the actual selling price. We also fall back to
             original_line_price when compare_at_price isn't available but
             the line has a discount applied (sale + auto-discount). */
          var qty = item.quantity || 1;
          var lineCurrent = (item.final_line_price != null) ? item.final_line_price : (item.final_price * qty);
          var lineCompare = 0;
          if (item.compare_at_price && item.compare_at_price > item.final_price) {
            lineCompare = item.compare_at_price * qty;
          } else if (item.original_line_price && item.original_line_price > lineCurrent) {
            lineCompare = item.original_line_price;
          }
          var priceHtml = formatMoney(lineCurrent);
          if (lineCompare > lineCurrent) {
            priceHtml = '<s>' + formatMoney(lineCompare) + '</s> ' + priceHtml;
          }

          /* Per-line discount badges — rendered for any line_level_discount_allocations
             present on this item. Mirrors the order-level discount badge styling but
             scoped to a single product so the customer can see exactly which item the
             discount is reducing. */
          var lineDiscountsHtml = (item.line_level_discount_allocations || []).map(function (alloc) {
            var title = (alloc.discount_application && alloc.discount_application.title) || 'Discount';
            var amount = alloc.amount || 0;
            return (
              '<div class="petal-cart-drawer__item-discount petal-cart-drawer__discount-row">' +
                '<span class="petal-cart-drawer__discount-code">🏷 ' + escapeHtml(title) + '</span>' +
                (amount > 0
                  ? '<span class="petal-cart-drawer__discount-amount">' + formatMoney(amount) + '</span>'
                  : '<span class="petal-cart-drawer__discount-amount">Applied</span>'
                ) +
              '</div>'
            );
          }).join('');

          row.innerHTML =
            '<div class="petal-cart-drawer__item-img">' +
              (imgUrl ? '<img src="' + imgUrl + '" alt="">' : '') +
            '</div>' +
            '<div class="petal-cart-drawer__item-body">' +
              '<h3 class="petal-cart-drawer__item-title">' + escapeHtml(item.product_title) + '</h3>' +
              (variantText ? '<p class="petal-cart-drawer__item-variant">' + escapeHtml(variantText) + '</p>' : '') +
              '<p class="petal-cart-drawer__item-price">' + priceHtml + '</p>' +
              '<div class="petal-cart-drawer__item-qty">' +
                '<button type="button" data-petal-cart-qty-dec aria-label="Decrease quantity">−</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-petal-cart-qty-inc aria-label="Increase quantity">+</button>' +
              '</div>' +
              lineDiscountsHtml +
            '</div>' +
            '<button type="button" class="petal-cart-drawer__item-remove" data-petal-cart-remove aria-label="Remove">' +
              '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"/></svg>' +
            '</button>';
          itemsEl.appendChild(row);
        });
      }

      // Reveal the Rewards + Apply-discount blocks only when the cart holds at
      // least one real (non-protection) item. itemCount already excludes the
      // protection line, so an empty cart — or one holding only the auto-added
      // protection — keeps both blocks hidden, per spec.
      var hasItems = totals.itemCount > 0;
      if (rewardsEl) rewardsEl.hidden = !hasItems;
      if (couponEl)  couponEl.hidden  = !hasItems;

      // Sync protection toggle state
      if (protToggle && protectionVariantId) {
        var hasProtection = cart.items.some(function (item) {
          return String(item.variant_id) === protectionVariantId;
        });
        protToggle.checked = hasProtection;
      }

      // Update the header cart count too
      document.querySelectorAll('[data-petal-cart-mirror]').forEach(function (el) {
        el.textContent = totals.itemCount;
      });
      document.querySelectorAll('.nymmind-cart-count').forEach(function (el) {
        if (totals.itemCount > 0) {
          el.textContent = totals.itemCount;
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    }

    function escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
      });
    }

    /* Render the discount-codes row. Pulls customer-applied codes from
       cart.discount_codes and any app/cart-level automatic discount titles
       from cart.cart_level_discount_applications. Hidden when nothing applied.
       Codes whose discounts are applied to specific line items are rendered
       INSIDE those item cards instead and excluded here via skipCodes. */
    function renderDiscounts(cart, skipCodes) {
      if (!discountsEl) return;
      skipCodes = skipCodes || {};
      var rows = [];
      (cart.discount_codes || []).forEach(function (d) {
        if (d && d.code && d.applicable !== false) {
          if (skipCodes[d.code.toUpperCase()]) return;
          rows.push({ label: d.code, amount: d.amount || 0 });
        }
      });
      // Cart-level automatic discounts (no code, name only).
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        if (!d) return;
        rows.push({ label: d.title || 'Discount', amount: d.total_allocated_amount || 0 });
      });
      if (!rows.length) {
        discountsEl.hidden = true;
        discountsEl.innerHTML = '';
        return;
      }
      discountsEl.hidden = false;
      discountsEl.innerHTML = rows.map(function (r) {
        return (
          '<div class="petal-cart-drawer__discount-row">' +
            '<span class="petal-cart-drawer__discount-code">🏷 ' + escapeHtml(r.label) + '</span>' +
            (r.amount > 0
              ? '<span class="petal-cart-drawer__discount-amount">−' + formatMoney(r.amount) + '</span>'
              : '<span class="petal-cart-drawer__discount-amount">Applied</span>'
            ) +
          '</div>'
        );
      }).join('');
    }

    /* Confetti burst: spawn ~36 colored squares from inside the panel,
       give each a random horizontal drift + rotation via CSS custom
       properties, animate via the .is-firing class, then clean up. */
    function fireFreeShippingCelebration() {
      if (!confettiEl) return;
      // Pulse the bar text
      if (shipbar) {
        shipbar.classList.remove('is-unlocked');
        // Force reflow so the animation can re-run
        void shipbar.offsetWidth;
        shipbar.classList.add('is-unlocked');
      }
      // Spawn pieces
      confettiEl.innerHTML = '';
      var colors = ['#4D4184', '#B6B8DC', '#43C271', '#F7A36A', '#F4D58D', '#8C8BBF'];
      var pieceCount = 36;
      var panelWidth = panel ? panel.getBoundingClientRect().width : 380;
      for (var i = 0; i < pieceCount; i++) {
        var piece = document.createElement('span');
        piece.className = 'petal-cart-drawer__confetti-piece';
        var startX = Math.random() * panelWidth;
        var driftX = (Math.random() * 220) - 110;       // -110..110 px
        var driftY = 480 + Math.random() * 220;         // 480..700 px
        var rotate = (Math.random() * 1080) - 540;      // -540..540 deg
        piece.style.left = startX + 'px';
        piece.style.background = colors[i % colors.length];
        piece.style.setProperty('--cx', driftX + 'px');
        piece.style.setProperty('--cy', driftY + 'px');
        piece.style.setProperty('--rot', rotate + 'deg');
        piece.style.animationDelay = (Math.random() * 220) + 'ms';
        confettiEl.appendChild(piece);
      }
      confettiEl.classList.remove('is-firing');
      void confettiEl.offsetWidth; // reflow
      confettiEl.classList.add('is-firing');
      // Clean up after the animation completes.
      setTimeout(function () {
        confettiEl.classList.remove('is-firing');
        confettiEl.innerHTML = '';
      }, 2200);
    }

    /* Apply a single discount code by navigating /discount/CODE inside a
       hidden iframe — the only reliable way to set Shopify's discount cookie
       from JS without a full-page redirect. Resolves once the iframe loads (or
       a 2.5s timeout, whichever comes first). Does NOT re-fetch the cart;
       callers chain fetchCart afterward. Shared by the session re-apply below
       and the customer-facing coupon field / tap-to-apply reward code. */
    function applyDiscountViaIframe(code) {
      return new Promise(function (resolve) {
        var iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
        var done = false;
        function finish() {
          if (done) return;
          done = true;
          try { iframe.parentNode && iframe.parentNode.removeChild(iframe); } catch (e) {}
          resolve();
        }
        iframe.addEventListener('load', finish);
        setTimeout(finish, 2500);
        iframe.src = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart?petal_applied=' + encodeURIComponent(code));
        document.body.appendChild(iframe);
      });
    }

    /* If the landing page stashed any FREE-addon discount codes in
       sessionStorage and the cart doesn't yet have them applied, re-apply
       them via a hidden iframe. Re-fetch the cart afterwards so the discount
       is reflected. */
    var discountReapplied = false;
    function maybeReapplyDiscountCodes(cart) {
      if (discountReapplied) return cart;
      var pending;
      try { pending = (sessionStorage.getItem('petal_discount_codes') || '').split(',').filter(Boolean); }
      catch (e) { pending = []; }
      if (!pending.length) return cart;
      var applied = (cart && cart.discount_codes || [])
        .filter(function (d) { return d.applicable; })
        .map(function (d) { return (d.code || '').toUpperCase(); });
      var missing = pending.filter(function (c) { return applied.indexOf(c.toUpperCase()) === -1; });
      if (!missing.length) return cart;
      discountReapplied = true; // only retry once per page load
      var chain = Promise.resolve();
      missing.forEach(function (code) { chain = chain.then(function () { return applyDiscountViaIframe(code); }); });
      return chain.then(fetchCart);
    }

    function refreshAndRender() {
      return fetchCart()
        .then(maybeReapplyDiscountCodes)
        .then(maybeAutoApplyProtection)
        .then(enrichCartCompareAt)
        .then(render)
        .catch(function () {});
    }

    /* Auto-apply Shipping Protection on the first cart fetch in a session,
       unless the customer has explicitly toggled it off. We track the
       opt-out in sessionStorage so it persists across page navigations
       within the same browsing session but resets on a new session. */
    function maybeAutoApplyProtection(cart) {
      if (!protectionVariantId || !protectionDefaultOn || protectionAutoApplied) return cart;
      protectionAutoApplied = true; // Run only once per page load
      try {
        if (sessionStorage.getItem(DISMISS_KEY) === '1') return cart;
      } catch (e) {}
      var hasIt = (cart.items || []).some(function (it) {
        return String(it.variant_id) === protectionVariantId;
      });
      if (hasIt) return cart;
      // Add it, then re-fetch and pass through the chain.
      return addToCart(protectionVariantId, 1)
        .then(fetchCart)
        .catch(function () { return cart; });
    }

    /* Fetch the protection product's featured image once and inject it
       into the protection row (replacing the SVG fallback). */
    function loadProtectionImage() {
      if (!protectionVariantId || !protectionHandle) return;
      var imgEl = drawer.querySelector('[data-petal-cart-protection-image]');
      var fallback = drawer.querySelector('[data-petal-cart-protection-icon-fallback]');
      if (!imgEl) return;
      fetch('/products/' + protectionHandle + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) {
          if (!product) return;
          var url = product.featured_image || (product.images && product.images[0]);
          if (!url) return;
          imgEl.src = url;
          imgEl.removeAttribute('hidden');
          if (fallback) fallback.style.display = 'none';
        })
        .catch(function () {});
    }
    loadProtectionImage();

    /* ---------- Open / close triggers ---------- */
    document.addEventListener('click', function (e) {
      // Open
      var openHit = e.target.closest('[data-petal-cart-open]');
      var cartLinkHit = e.target.closest('a[href="/cart"], a[href$="/cart"]');
      if (openHit || cartLinkHit) {
        e.preventDefault();
        show();
        refreshAndRender();
        return;
      }
      // Close
      if (e.target.closest('[data-petal-cart-close]')) {
        hide();
        return;
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) hide();
    });

    /* External open: any code can call
         document.dispatchEvent(new CustomEvent('petal:cart:open'))
       to refresh the drawer state and slide it in. Used by the landing
       page's Add-to-Cart handler after a successful /cart/add.js. */
    document.addEventListener('petal:cart:open', function () {
      refreshAndRender().then(function () { show(); });
    });

    /* ---------- Add-to-cart hijack ---------- */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = (form.getAttribute('action') || '').split('?')[0];
      if (!/\/cart\/add$/i.test(action)) return;
      e.preventDefault();

      /* Loading feedback on the button that triggered this add. Stock
         Shrine/Dawn buttons (product page, sticky ATC, featured product,
         quick-add) ship a hidden `.loading-overlay__spinner` inside their
         submit button; we un-hide it while the /cart/add.js POST + drawer
         render runs, then restore it in `.finally` — i.e. exactly while the
         drawer is showing up. We GATE on that spinner element's presence so
         this only ever touches stock theme buttons; the custom
         petal/flamingo landing buttons add via their own fetch (no form
         submit) and have their own "Adding…" state, so they are untouched. */
      var submitBtn = (e.submitter && e.submitter.form === form)
        ? e.submitter
        : (form.querySelector('button[name="add"][type="submit"]')
            || form.querySelector('.product-form__submit')
            || form.querySelector('button[type="submit"], [type="submit"]'));
      var btnSpinner = submitBtn ? submitBtn.querySelector('.loading-overlay__spinner') : null;
      if (btnSpinner) {
        /* Re-entry guard: ignore repeat submits while this button's add is
           still in flight. The document-level listener has no other debounce,
           so without this a rapid double-click would fire two /cart/add.js
           POSTs and flicker the spinner. preventDefault already ran above, so
           returning fully swallows the duplicate submit. Scoped to stock
           buttons (gated on the spinner) so custom forms are unaffected. */
        if (submitBtn.getAttribute('aria-disabled') === 'true') return;
        submitBtn.classList.add('loading');
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.setAttribute('aria-busy', 'true');
        btnSpinner.classList.remove('hidden');
      }
      function clearButtonLoading() {
        if (!btnSpinner) return;
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
        submitBtn.removeAttribute('aria-busy');
        btnSpinner.classList.add('hidden');
      }

      var fd = new FormData(form);
      // Record this variant in the client-side insertion-order map (no line
      // properties; properties would split duplicate adds into separate lines).
      try {
        var fdVariantId = String(fd.get('id') || '').trim();
        if (fdVariantId) {
          var orderMap = {};
          try { orderMap = JSON.parse(sessionStorage.getItem('petal_cart_order') || '{}') || {}; } catch (eParse) {}
          if (orderMap[fdVariantId] === undefined) {
            var maxSeq = 0;
            for (var ok in orderMap) { if (orderMap[ok] > maxSeq) maxSeq = orderMap[ok]; }
            orderMap[fdVariantId] = maxSeq + 1;
            sessionStorage.setItem('petal_cart_order', JSON.stringify(orderMap));
          }
        }
      } catch (e) {}
      fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (!res.ok) {
            console.warn('Cart add failed', res.data);
            return;
          }
          return refreshAndRender().then(function () { show(); });
        })
        .catch(function (err) { console.warn('Cart add error', err); })
        .finally(clearButtonLoading);
    });

    /* ---------- Item mutations (event delegation) ---------- */
    drawer.addEventListener('click', function (e) {
      var row = e.target.closest('[data-line]');
      if (!row) return;
      var line = parseInt(row.getAttribute('data-line'), 10);
      if (e.target.closest('[data-petal-cart-qty-inc]')) {
        var nextQty = parseInt(row.querySelector('.petal-cart-drawer__item-qty span').textContent, 10) + 1;
        changeLine(line, nextQty).then(enrichCartCompareAt).then(render);
      } else if (e.target.closest('[data-petal-cart-qty-dec]')) {
        var prevQty = Math.max(0, parseInt(row.querySelector('.petal-cart-drawer__item-qty span').textContent, 10) - 1);
        changeLine(line, prevQty).then(enrichCartCompareAt).then(render);
      } else if (e.target.closest('[data-petal-cart-remove]')) {
        changeLine(line, 0).then(enrichCartCompareAt).then(render);
      }
    });

    /* ---------- Shipping protection toggle ----------
       Manual toggle persists the opt-out flag in sessionStorage so the
       auto-apply on subsequent cart fetches doesn't re-add what the
       customer just removed. Clearing the flag when re-enabled means a
       customer who toggles off then on resumes auto-apply behavior. */
    if (protToggle && protectionVariantId) {
      protToggle.addEventListener('change', function () {
        if (protToggle.checked) {
          try { sessionStorage.removeItem(DISMISS_KEY); } catch (e) {}
          addToCart(protectionVariantId, 1).then(refreshAndRender);
        } else {
          try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
          changeByVariant(protectionVariantId, 0).then(enrichCartCompareAt).then(render);
        }
      });
    }

    /* ---------- Apply discount code (coupon field + tap-to-apply reward code) ----------
       Reuses the hidden-iframe /discount/CODE method, then re-fetches the cart
       and inspects cart.discount_codes to confirm the code actually took. A
       valid code then renders in the existing discounts row above. */
    var couponBusy = false;
    function setCouponMsg(text, kind) {
      if (!couponMsg) return;
      couponMsg.classList.remove('is-error', 'is-success');
      if (!text) { couponMsg.hidden = true; couponMsg.textContent = ''; return; }
      couponMsg.hidden = false;
      couponMsg.textContent = text;
      if (kind) couponMsg.classList.add('is-' + kind);
    }
    function applyCouponCode(rawCode) {
      var code = String(rawCode == null ? '' : rawCode).trim();
      if (!code || couponBusy) return;
      couponBusy = true;
      setCouponMsg('Applying…', null);
      var applyBtn = drawer.querySelector('[data-petal-cart-coupon-apply]');
      if (applyBtn) applyBtn.setAttribute('aria-busy', 'true');
      applyDiscountViaIframe(code)
        .then(fetchCart)
        .then(function (cart) {
          var match = (cart.discount_codes || []).filter(function (d) {
            return d && d.code && String(d.code).toUpperCase() === code.toUpperCase();
          })[0];
          var ok = !!(match && match.applicable !== false);
          return enrichCartCompareAt(cart).then(function (c) { render(c); return ok; });
        })
        .then(function (ok) {
          if (ok) {
            setCouponMsg('Code applied', 'success');
            if (couponInput) couponInput.value = '';
          } else {
            setCouponMsg('That code isn’t valid for this cart.', 'error');
          }
        })
        .catch(function () { setCouponMsg('Something went wrong — try again.', 'error'); })
        .finally(function () {
          couponBusy = false;
          if (applyBtn) applyBtn.removeAttribute('aria-busy');
        });
    }
    if (couponForm) {
      couponForm.addEventListener('submit', function (e) {
        e.preventDefault();
        applyCouponCode(couponInput ? couponInput.value : '');
      });
    }

    /* Balloon-pop a reward card when it's added: a quick inflate -> burst with
       a small brand-colored confetti spray, then the card is removed and the
       grid reflows to the remaining cards (or the whole rewards block hides
       once none remain). Pure visual; the add-to-cart already succeeded. */
    function spawnPopBurst(card) {
      if (!card) return;
      var burst = document.createElement('span');
      burst.className = 'petal-pop-burst';
      var colors = ['#4D4184', '#B6B8DC', '#43C271', '#8C8BBF', '#EDEEFA', '#6B5FA1'];
      var n = 12;
      for (var i = 0; i < n; i++) {
        var piece = document.createElement('span');
        piece.className = 'petal-pop-piece';
        var ang = (Math.PI * 2 * i) / n;
        var dist = 30 + (i % 4) * 11;
        piece.style.setProperty('--px', (Math.cos(ang) * dist).toFixed(1) + 'px');
        piece.style.setProperty('--py', (Math.sin(ang) * dist).toFixed(1) + 'px');
        piece.style.setProperty('--pr', ((i * 53) % 360) + 'deg');
        piece.style.background = colors[i % colors.length];
        piece.style.animationDelay = ((i % 3) * 25) + 'ms';
        burst.appendChild(piece);
      }
      card.appendChild(burst);
    }
    function popAndRemoveRewardCard(card) {
      if (!card || card.getAttribute('data-popping') === '1') return;
      card.setAttribute('data-popping', '1');
      var grid = card.parentNode;
      var rewardsBlock = card.closest('[data-petal-cart-rewards]');
      spawnPopBurst(card);
      card.classList.add('is-popping');
      setTimeout(function () {
        if (card.parentNode) card.parentNode.removeChild(card);
        if (!grid) return;
        var remaining = grid.querySelectorAll('.petal-cart-drawer__reward-card').length;
        if (remaining > 0) {
          grid.style.gridTemplateColumns = 'repeat(' + remaining + ', minmax(0, 1fr))';
        } else if (rewardsBlock) {
          rewardsBlock.hidden = true; // nothing left to offer
        }
      }, 740);
    }

    /* ---------- Rewards: tap-to-apply code + one-tap add-to-cart ----------
       Separate delegated listener from the qty/remove one above; reward
       controls live outside any [data-line] row, so that handler ignores them. */
    drawer.addEventListener('click', function (e) {
      var codeBtn = e.target.closest('[data-petal-reward-code]');
      if (codeBtn) {
        applyCouponCode(codeBtn.getAttribute('data-code') || '');
        return;
      }
      var addBtn = e.target.closest('[data-petal-reward-add]');
      if (!addBtn || addBtn.disabled) return;
      if (addBtn.getAttribute('aria-busy') === 'true') return;
      var vid = addBtn.getAttribute('data-variant-id');
      if (!vid) return;
      var card = addBtn.closest('.petal-cart-drawer__reward-card');
      addBtn.setAttribute('aria-busy', 'true');
      var orig = addBtn.textContent;
      addBtn.textContent = 'Adding…';
      // Record insertion order so the new line sorts last (parity with the
      // stock add-to-cart hijack above).
      try {
        var orderMap = JSON.parse(sessionStorage.getItem('petal_cart_order') || '{}') || {};
        if (orderMap[String(vid)] === undefined) {
          var maxSeq = 0;
          for (var ok2 in orderMap) { if (orderMap[ok2] > maxSeq) maxSeq = orderMap[ok2]; }
          orderMap[String(vid)] = maxSeq + 1;
          sessionStorage.setItem('petal_cart_order', JSON.stringify(orderMap));
        }
      } catch (eOrder) {}
      addToCart(vid, 1)
        .then(function () {
          // Success: balloon-pop the card, then remove it from the rewards row.
          popAndRemoveRewardCard(card);
          return refreshAndRender();
        })
        .catch(function () { addBtn.textContent = orig; addBtn.removeAttribute('aria-busy'); });
    });

    /* Initial sync of header badge with cart contents */
    refreshAndRender();
  });
})();
